import router from '@/router'
import { t } from '@/locales/i18n'
import { omit } from 'lodash'
import {
  sanitizeQuery,
  getSoltabQuery,
  getBoardQuery,
  getFullHandSetupQuery,
  getDialogsQuery,
  getHistoryQuery,
  getBoardFilterQuery,
  getFullHandSetupOptions,
  getFullHandSetupConfig,
  getDefaultQuery,
  getFullHandPreferencesOptions,
  getBoardFilterOptions,
  getFullHandGameOptions,
  getCustomSolutionQuery,
  getRouteNameByStudyTab,
} from '@/store/router/router'
import { getSortedBoard, getVisibleBoard } from '@/helpers/cards'
import {
  getGameformatQuery,
  getGameformat,
  getGmfFilterQuery,
  getGmfTableQuery,
} from '@/store/gameformat/gameformat'
import { getPokerPositions } from '@/store/gamestate/gamestate'

const boardFilterOptions = getBoardFilterOptions()

const fullHandGameOptions = getFullHandGameOptions()
const fullHandPreferencesOptions = getFullHandPreferencesOptions()
const fullHandSetupOptions = getFullHandSetupOptions() // includes preferences and game options

function getSessionQuery() {
  const fhSetupDefaults = getDefaultQuery(getFullHandSetupConfig())
  const res = {
    // hand_detail_tmp: '', // analyzer
    // trainer_hand_detail_tmp: '', // trainer-analyzer
    // trainer_practice_hand_detail_tmp: '', // trainer-analyzer

    custree_id: '',
    cussol_id: '',
    solution_type: '', // gwiz, custom
    gmfs_solution_tab: '', // ai_sols, ai_trees, ai_reports
    soltab: '', // strategy, range, breakdown, reports

    // cussavedtree_id: '', // used in trainer to create new tree from saved tree
    // gametype params
    gametype: '',
    depth: '',
    stacks: '',

    // params for choosing stack,depth,avg_stack lists
    depth_list: '', // gmfs_depthList
    stacks_list: '', // gmfs_stacksList
    average_stack: '', // gmfs_avgStackList

    gmff_stacks_type: '',

    gmff_opening_type: '',
    gmff_tournament_phase: '',
    gmff_players: '',
    gmff_variant: '',

    gmff_cash_drop: '',

    gmff_biggest_stack: '',
    gmff_depth: '',
    gmff_type: '',
    gmff_rake: '',
    gmff_opening_size: '',
    gmff__3bet_size: '',
    gmff_simplified_position: '',
    gmff_favorite: '',

    gmfft_sort_key: '',
    gmfft_sort_order: '',

    preflop_actions: '',
    flop_actions: '',
    turn_actions: '',
    river_actions: '',
    history_spot: '',

    board: '',
    hand: '',
    board_tmp: '',
    hand_tmp: '',
    hand_op_tmp: '',

    flop_suits: '',
    flop_paired: '',
    flop_connectedness: '',
    flop_subset: '',
    flop_high_card: '',
    flop_mid_card: '',
    flop_low_card: '',
    turn_suit: '',
    turn_paired: '',
    turn_card: '',
    river_suit: '',
    river_paired: '',
    river_card: '',

    ...fhSetupDefaults,
  }
  return res
}

function getDisplayQuery() {
  return {
    dialogs: '',
  }
}

function getInitialQuery() {
  return {
    ...getSessionQuery(),
    ...getDisplayQuery(),
  }
}

function getInitialState({ namespace }) {
  const res = {
    router_namespace: namespace,
    path_name: '',
    from_path_name: '/',

    ...getInitialQuery(namespace),
  }
  return res
}

const actions = {
  async router_initialize(
    { state, dispatch, getters, rootGetters },
    { customQuery, localOnly = false, lsKey } = {},
  ) {
    const namespace = state.router_namespace
    lsKey = lsKey || namespace || getRouteNameByStudyTab(router, rootGetters)

    const gameformats = rootGetters.gameformats
    const { query: gameformatQuery, gameformat } = getGameformatQuery({
      gameformats,
      lsKey,
      localOnly,
    })

    // console.log('gameformatQuery', gameformatQuery, localOnly, state.router_namespace)

    const gmfFilterQuery = getGmfFilterQuery({ lsKey, localOnly })
    const gmfTableQuery = getGmfTableQuery({ lsKey, localOnly })
    const availablePositions = getPokerPositions({ gameformat })
    const initialQuery = getInitialQuery(namespace)
    const historyQuery = getHistoryQuery({ lsKey, localOnly })
    const board = getBoardQuery({ lsKey, key: 'board', localOnly })
    const boardFilterQuery = getBoardFilterQuery({ lsKey, localOnly })
    const fullhandSetupQuery = getFullHandSetupQuery({ lsKey, localOnly, availablePositions })
    const dialogs = getDialogsQuery()
    const soltab = getSoltabQuery(rootGetters.user_has_flag('CR_REPORTS'))
    const solTypeQuery = getCustomSolutionQuery({ lsKey, localOnly })

    const query = {
      ...initialQuery,
      ...gameformatQuery,
      ...gmfFilterQuery,
      ...gmfTableQuery,
      ...historyQuery,
      ...solTypeQuery,
      ...board,
      ...boardFilterQuery,
      ...fullhandSetupQuery,
      ...dialogs,
      ...soltab,
      ...customQuery,
    }

    // console.log('router_initialize', query, state.router_namespace, localOnly)
    // debugger
    dispatch('router_update_query', {
      query,
      push: false,
      replace: false,
      saveLocally: false,
    })
  },
  router_method(
    { state, commit },
    { query = router.currentRoute.value.query, method = 'push', path_name },
  ) {
    router[method]({ name: path_name, query }).catch(() => {})
    // console.log('router_method', method, path_name, query)
  },
  router_update_query(
    { state, commit, dispatch, getters, rootGetters },
    { query, push = true, replace = true, saveLocally = true, path_name },
  ) {
    const lsPathName = path_name || getRouteNameByStudyTab(router, rootGetters)
    const existingPathName = lsPathName === 'StudyReports' ? 'StudySolutions' : lsPathName
    commit('router_update', {
      path_name: existingPathName,
      query,
    })

    // debugger
    const urlQuery = sanitizeQuery({
      ...router.currentRoute.value.query,
      ...getters.query_query,
      ...query,
    })

    if (existingPathName !== 'PracticeTrainer') {
      Object.keys(urlQuery).forEach(key => {
        if (key.includes('fh_')) {
          delete urlQuery[key]
        }
      })
    }
    if (!['StudySolutions', 'StudyReports'].includes(lsPathName)) {
      Object.keys(urlQuery).forEach(key => {
        if (key.includes('soltab')) {
          delete urlQuery[key]
        }
      })
    }

    if (push) {
      dispatch('router_method', {
        method: 'push',
        path_name: existingPathName,
        query: urlQuery,
      })
    } else if (replace) {
      dispatch('router_method', {
        method: 'replace',
        path_name: existingPathName,
        query: urlQuery,
      })
    }

    const namespace = state.router_namespace
    if (saveLocally && !namespace?.includes('tmp')) {
      const lsQuery = sanitizeQuery({
        ...getters.query_query,
        ...query,
      })

      // in trainer we use tra/aplha instead of PracticeTrainer
      let lsKey = namespace || lsPathName

      if (existingPathName === 'StudySolutions' && rootGetters.user_has_flag('CR_REPORTS')) {
        lsKey = lsPathName // StudySolutions or StudyReports
      }

      localStorage.setItem(lsKey, JSON.stringify(lsQuery))
    }
  },
  router_update_query_dialogs(
    { state, commit, dispatch, getters, rootGetters },
    { query, path_name },
  ) {
    setTimeout(() => {
      const lsPathName = path_name || getRouteNameByStudyTab(router, rootGetters)
      const existingPathName = lsPathName === 'StudyReports' ? 'StudySolutions' : lsPathName
      commit('router_update', {
        path_name: existingPathName,
        query,
      })
      const urlQuery = {
        ...router.currentRoute.value.query,
        ...query,
      }
      dispatch('router_method', {
        method: 'replace',
        path_name: existingPathName,
        query: urlQuery,
      })
    })
  },

  router_set_metatitle({ getters }, { to } = {}) {
    to = to || router.currentRoute.value
    const { title } = document
    let shouldBeTitle = to.meta.title
    if (to.name === 'StudySolutions') {
      const { query_board, street } = getters
      const board = query_board.slice(0, getVisibleBoard(street))
      const streetTranslated = t(`common.${street}`)
      shouldBeTitle = `${street === 'preflop' ? 'Preflop' : board || streetTranslated || ''} - ${
        to.meta.title
      }`
    }
    if (shouldBeTitle && title !== shouldBeTitle) {
      document.title = shouldBeTitle
    }
    // else will be set in component
    // StudyPlanPage
  },
  router_set_dialogs({ state, dispatch, getters }, dialogs) {
    const query_dialogs = router.currentRoute.value.query.dialogs
    let res = (query_dialogs || '').split(',').filter(i => i)
    res = [...res, dialogs]
    res.reverse()
    res = [...new Set(res)]
    res.reverse()
    res = res.join(',')
    dispatch('router_update_query_dialogs', {
      query: { dialogs: res },
    })
  },
  router_rm_dialog({ state, dispatch, getters }, dialog) {
    dispatch('router_rm_dialogs', { dialogs: [dialog] })
  },
  router_rm_dialogs({ state, dispatch, getters }, { dialogs }) {
    const query_dialogs = router.currentRoute.value.query.dialogs
    let l_dialogs = (query_dialogs || '').split(',').filter(i => i)
    l_dialogs = l_dialogs.filter(d => !dialogs.some(dd => d.includes(dd)))
    l_dialogs = l_dialogs.join(',')
    dispatch('router_update_query_dialogs', {
      query: {
        dialogs: l_dialogs,
      },
    })
  },
  router_reset_dialogs({ state, dispatch, getters }, { exeptions = null } = {}) {
    if (!router.currentRoute.value.query.dialogs) return
    let res = ''
    if (exeptions) {
      res = router.currentRoute.value.query.dialogs.split(',')
      res = res.filter(d => exeptions.some(e => d.includes(e)))
      res = [...new Set(res)]
      res = res.join(',')
    }

    dispatch('router_update_query_dialogs', {
      query: {
        dialogs: res,
      },
    })
  },
  router_reset({ state, commit }, initialState) {
    commit('router_reset', initialState)
  },
  router_set({ state, commit }, { key, value }) {
    commit('router_set', { key, value })
  },
}

const getters = {
  router_get: state => key => state[key],
  router_query: state => {
    const query = omit(state, ['from_path_name', 'path_name', 'router_namespace'])
    const res = {
      ...router.currentRoute.value.query,
      ...query,
    }
    return res
  },
  query_query: state => omit(state, ['from_path_name', 'path_name', 'router_namespace', 'dialogs']),
  path_name: state => state.path_name,
  query_dialogs: state => state.dialogs || '',
  query_board: state => getSortedBoard(state.board),
  query_board_tmp: state => getSortedBoard(state.board_tmp),
  query_hand: state => state.hand,
  query_hand_tmp: state => state.hand_tmp,
  query_hand_op_tmp: state => state.hand_op_tmp,
  query_history_spot: state => state.history_spot,
  query_cards: state => {
    const res = {
      board: getSortedBoard(state.board),
      board_tmp: getSortedBoard(state.board_tmp),
      hand: state.hand,
      hand_tmp: state.hand_tmp,
      hand_op_tmp: state.hand_op_tmp,
    }
    return res
  },
  query_gameformat: state => {
    const res = {
      gametype: state.gametype,
      depth: state.depth,
      stacks: state.stacks,
      depth_list: state.depth_list,
      stacks_list: state.stacks_list,
      average_stack: state.average_stack,
    }
    return res
  },
  query_gmfFilter: state => {
    const res = {
      gmff_stacks_type: state.gmff_stacks_type,

      gmff_opening_type: state.gmff_opening_type,
      gmff_tournament_phase: state.gmff_tournament_phase,
      gmff_players: state.gmff_players,
      gmff_variant: state.gmff_variant,

      gmff_biggest_stack: state.gmff_biggest_stack,
      gmff_depth: state.gmff_depth,
      gmff_cash_drop: state.gmff_cash_drop,
      gmff_type: state.gmff_type,
      gmff_rake: state.gmff_rake,
      gmff_opening_size: state.gmff_opening_size,
      gmff__3bet_size: state.gmff__3bet_size,
      gmff_simplified_position: state.gmff_simplified_position,
      gmff_favorite: state.gmff_favorite,
    }
    return res
  },
  query_gmfTable: state => {
    const res = {
      gmfft_sort_key: state.gmfft_sort_key,
      gmfft_sort_order: state.gmfft_sort_order,
    }
    return res
  },
  query_gmfSelector: (state, getters) => {
    const res = {
      ...getters.query_gameformat,
      ...getters.query_gmfFilter,
      ...getters.query_gmfTable,
      solution_type: state.solution_type,
      gmfs_solution_tab: state.gmfs_solution_tab,
    }
    return res
  },
  // // TODO rename thi exeption in naming
  gameformat: (state, getters, rootState, rootGetters) => {
    // GMFoptimized
    // compare with gmfs_gameformat else find it amongst thousands of gameformats
    // could be further optimized by splitting gameformats by gametype
    const res =
      (rootGetters.gmfs_gameformat
        ? getGameformat({
            item: getters.query_gameformat,
            gameformats: [rootGetters.gmfs_gameformat],
          })
        : null) ||
      getGameformat({
        item: getters.query_gameformat,
        gameformats: rootGetters.gameformats,
      })
    return res
  },

  query_soltab: state => state.soltab,
  query_custree_id: state => state.custree_id,
  query_cussol_id: state => state.cussol_id,
  query_solution_type: state => state.solution_type,
  query_gmfs_solution_tab: state => state.gmfs_solution_tab,
  query_cussol: state => {
    const res = {
      custree_id: state.custree_id,
      cussol_id: state.cussol_id,
      solution_type: state.solution_type,
    }
    return res
  },

  query_history: state => {
    const res = {
      custree_id: state.custree_id,
      cussol_id: state.cussol_id,
      solution_type: state.solution_type,
      gametype: state.gametype,
      depth: state.depth,
      stacks: state.stacks,
      preflop_actions: state.preflop_actions || '',
      flop_actions: state.flop_actions || '',
      turn_actions: state.turn_actions || '',
      river_actions: state.river_actions || '',
      history_spot: state.history_spot || '',
    }
    return res
  },
  query_study_history: state => {
    const res = {
      custree_id: state.custree_id,
      cussol_id: state.cussol_id,
      solution_type: state.solution_type,
      gametype: state.gametype,
      depth: state.depth,
      stacks: state.stacks,
      preflop_actions: state.preflop_actions || '',
      flop_actions: state.flop_actions || '',
      turn_actions: state.turn_actions || '',
      river_actions: state.river_actions || '',
      history_spot: state.history_spot || '',
      board: state.board || '',
    }
    return res
  },
  query_board_filter: state => {
    const res = {}
    Object.keys(boardFilterOptions).forEach(key => {
      res[key] = state[key] || ''
    })
    return res
  },
  query_fullhand_game: state => {
    const res = {}
    Object.keys(fullHandGameOptions).forEach(key => {
      res[key] = state[key] || ''
    })
    return res
  },
  query_fullhand_preferences: state => {
    const res = {}
    Object.keys(fullHandPreferencesOptions).forEach(key => {
      res[key] = state[key] || ''
    })
    return res
  },
  query_fullhand_setup: state => {
    const res = {}
    Object.keys(fullHandSetupOptions).forEach(key => {
      res[key] = state[key] || ''
    })
    return res
  },
  query_trainer_standoff: (state, getters) => {
    const res = {
      fh_hero_tmp: state.fh_hero_tmp,
      fh_action_tmp: state.fh_action_tmp,
      fh_opponent_tmp: state.fh_opponent_tmp,
      depth: state.depth,
      solution_type: state.solution_type,
    }
    return res
  },
  query_trainer: (state, getters) => {
    const res = {
      ...getters.query_cards,
      ...getters.query_cussol,
      ...getters.query_gameformat,
      ...getters.query_history,
      ...getters.query_board_filter,
      ...getters.query_fullhand_setup,
      // no favorites in trainer only checkboxes,
      // no need to rember table ordering so thats disabled as well
      ...getters.query_gmfFilter,
      // ...getters.query_gmfTable,
      gmff_favorite: '',
    }
    return res
  },
  query_trainer_history: (state, getters) => {
    const res = {
      ...getters.query_cards,
      ...getters.query_gameformat,
      ...getters.query_cussol,
      ...getters.query_history,
      ...getters.query_board_filter,
      ...getters.query_fullhand_game,
      fh_trainer_mode: getters.query_fullhand_setup.fh_trainer_mode,
      fh_trainer_game_mode: getters.query_fullhand_setup.fh_trainer_game_mode,
    }
    return res
  },

  fh_start_spot: state => state.fh_start_spot,
  fh_trainer_grouping: state => state.fh_trainer_grouping,
  fh_trainer_autoplay: state => state.fh_trainer_autoplay,
  fh_trainer_mode: state => state.fh_trainer_mode,
  fh_trainer_game_mode: state => state.fh_trainer_game_mode,
  fh_trainer_session: state => Number(state.fh_trainer_session),
  fh_trainer_streak_visible: state => state.fh_trainer_streak_visible,
  fh_trainer_tables: (state, getters, rootState, rootGetters) =>
    rootGetters.globals_mobile ? 1 : Number(state.fh_trainer_tables),
  fh_trainer_game_speed: state => state.fh_trainer_game_speed,
  fh_trainer_timebank: state => state.fh_trainer_timebank,
  // fh_trainer_sounds: (state) => state.fh_trainer_sounds,
}

const mutations = {
  router_reset(state, initialState) {
    Object.assign(state, initialState || getInitialState({ namespace: state.router_namespace }))
  },
  router_set(state, { key, value }) {
    state[key] = value
  },
  router_update(state, { path_name, query }) {
    if (state.path_name !== path_name) {
      state.path_name = path_name
    }
    Object.keys(query).forEach(key => {
      if (state[key] !== query[key]?.toString()) {
        state[key] = query[key]?.toString()
      }
    })
  },
}

function getRouterModule(namespace) {
  return {
    state: getInitialState({ namespace }),
    getters,
    actions,
    mutations,
  }
}

export { getRouterModule }

// export default {
//   state,
//   getters,
//   actions,
//   mutations,
// }
